<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0;">
        <!-- Two siblings: content + footer -->
        
        <!-- Content -->
        <v-container 
            class="flex-grow-1 d-flex flex-column" 
            style="min-height: 0; height: 100%;"
        >
        <DataFetchLoadingComponent :isLoading="isLoadingFirestore" />
        
        <!-- Fixed header -->
        <v-row class="flex-shrink-0" v-if="currentProject || isUserOutOfOffice">
            <v-col cols="12">
                <OngoingProjectDetails
                    :color="
                        isUserOutOfOffice
                            ? 'orange-lighten-5'
                            : currentProject?.projectId === 'SYSTEM_LUNCH-BREAK'
                            ? 'warning'
                            : 'primary'
                    "
                    :variant="'flat'"
                    :custom-styles="{ padding: '20px' }"
                    :userName="user.name"
                    :surname="user.surname"
                    :project-name="currentProject?.name"
                    :start-time="this.activeEntry?.startTime"
                    :isEditable="false"
                    :project="currentProject"
                    :showUserDescription="showUserDescription"
                    :userDescriptionDisplay="true"
                    :description="activeEntryDescription"
                    @click="toggleDescriptionDisplay"
                    :outOfOffice="isUserOutOfOffice"
                />
            </v-col>
        </v-row>
        <v-row class="flex-shrink-0" v-else>
            <v-col cols="12">
                <h2>{{ user.name }} {{ user.surname }} ({{ user.role }})</h2>
            </v-col>
        </v-row>
        
        <!-- DayNavigator -->
        <v-row class="flex-shrink-0 pb-0" v-if="isEditingTimeEntries">
            <v-col cols="12">
                <DayNavigator
                    v-model="entriesDate"
                    @update:modelValue="onDateChange"
                />
            </v-col>
        </v-row>
        <v-row class="flex-shrink-0 pb-0">
            <v-col cols="12">
                <v-expand-transition>
                    <DayNavigator
                        v-if="!isEditingTimeEntries && tab === 'today'"
                        v-model="entriesDate"
                        @update:modelValue="onDateChange"
                    />
                </v-expand-transition>
            </v-col>
        </v-row>
        
        <!-- Tabs -->
        <v-tabs v-model="tab" color="primary" class="flex-shrink-0">
            <v-tab
                class="d-flex align-center"
                value="today"
                width="33%"
            >
                <span>{{ formattedDate }}</span>
            </v-tab>
            <v-tab
                value="projects"
                width="33%"
                :disabled="isEditingTimeEntries"
            >
                <div class="d-flex align-center">
                    <span
                        >Projects
                        <span class="text-primary">{{
                            periodSuffixLabel
                        }}</span></span
                    >
                    <!-- inject our chooser -->
                    <ProjectsPeriodFilterMenu
                        class="ml-2"
                        :projectNames="doProjectMap"
                        :priorityMap="doPriorityMap"
                        :disabled="tab !== 'projects'"
                        :userId="userId"
                        :projectIds="
                            projectsForReport.map((p) => p.projectId)
                        "
                        @period-changed="handlePeriodChange"
                    /></div
            ></v-tab>
            <v-tab
                value="settings"
                :disabled="isEditingTimeEntries"
                width="34%"
                >Settings</v-tab
            >
        </v-tabs>
        
        <!-- Tab Content -->
        <v-tabs-window 
            v-model="tab" 
            class="flex-grow-1 d-flex flex-column" 
            style="min-height: 0; height: 100%; overflow: visible;"
        >
            <!-- Today Tab Content -->
            <v-tabs-window-item 
                v-if="tab === 'today'"
                value="today"
                class="d-flex flex-column flex-grow-1" 
                style="min-height: 0; height: 100%;"
            >
                <v-row class="flex-grow-1" style="min-height: 0;">
                    <v-col class="d-flex flex-column" style="height: 100%; min-height: 0;">
                        <ProjectsTable
                            v-if="!isEditingTimeEntries"
                            :tableData="extendedTodayReportData"
                            :totalTimeWorked="totalTimeWorked"
                            :currentTab="tab"
                            :enableReordering="false"
                            :enablePriority="false"
                            :enableClickNavigation="false"
                        />
                        <EditableTimeEntries
                            v-if="isEditingTimeEntries"
                            ref="editEntries"
                            :timeEntries="currentTimeEntries"
                            :totalTimeWorked="totalTimeWorkedForEdit"
                            :projects="projectsForReport"
                            :selectedDate="entriesDate"
                            :user="user"
                        />
                    </v-col>
                </v-row>
            </v-tabs-window-item>

            <!-- Projects Tab Content -->
            <v-tabs-window-item 
                v-if="tab === 'projects'"
                value="projects"
                class="d-flex flex-column flex-grow-1" 
                style="min-height: 0; height: 100%;"
            >
                <v-row class="flex-grow-1" style="min-height: 0;">
                    <v-col class="d-flex flex-column" style="height: 100%; min-height: 0;">
                        <ProjectsTable
                            :tableData="extendedProjectsReportData"
                            @order-changed="updateTableReportData"
                            :currentTab="tab"
                            :enableReordering="true"
                            :enablePriority="true"
                            :enableClickNavigation="true"
                            @update-priority="handlePriorityUpdate"
                        />
                    </v-col>
                </v-row>
            </v-tabs-window-item>
            
            <!-- Settings Tab Content -->
            <v-tabs-window-item 
                v-if="tab === 'settings'"
                value="settings"
                class="d-flex flex-column flex-grow-1" 
                style="min-height: 0; height: 100%;"
            >
                <UserProfileForm
                    :userId="userId"
                    :showAccountStatus="
                        rights?.canChangeAccountStatus === true
                    "
                    :canChangeAccountStatus="
                        rights?.canChangeAccountStatus === true
                    "
                />
            </v-tabs-window-item>
        </v-tabs-window>
        
        <!-- Snackbar for Notifications -->
        <v-row class="flex-shrink-0">
            <v-col>
                <MessageSnackbar
                    :message="snackbar.message"
                    :color="snackbar.color"
                    :timeout="snackbar.timeout"
                    :show="snackbar.show"
                    @update:show="snackbar.show = $event"
                />
            </v-col>
        </v-row>
    </v-container>
    
    <!-- Footer - conditionally visible -->
    <BottomButtonWrapper :isShowing="shouldShowFooter">
        <!-- Today tab content -->
        <template v-if="tab === 'today'">
            <!-- NOT EDITING: two equal columns -->
            <template v-if="!isEditingTimeEntries">
                <v-col cols="6" class="d-flex justify-center">
                    <ThemedActionButton
                        :buttonColor="'success'"
                        :text="'BACK'"
                        @click="goBack"
                    />
                </v-col>
                <v-col cols="6" class="d-flex justify-center">
                    <ThemedActionButton
                        :buttonColor="'error'"
                        :text="'EDIT HOURS'"
                        @click="showEditableTimeEntries"
                    />
                </v-col>
            </template>

            <!-- EDITING: one or two columns; center when single -->
            <template v-else>
                <v-col
                    :cols="
                        currentTimeEntries.length === 0 ? 6 : 12
                    "
                    class="d-flex justify-center"
                >
                    <ThemedActionButton
                        :buttonColor="'error'"
                        :text="'BACK'"
                        @click="exitEditMode"
                    />
                </v-col>

                <v-col
                    v-if="currentTimeEntries.length === 0"
                    cols="6"
                    class="d-flex justify-center"
                >
                    <ThemedActionButton
                        :buttonColor="'primary'"
                        :text="'ADD ENTRY'"
                        @click="
                            $refs.editEntries?.addFirstEntry()
                        "
                    />
                </v-col>
            </template>
        </template>
        
        <!-- Projects tab content -->
        <template v-if="tab === 'projects'">
            <v-col class="d-flex justify-center" cols="6">
                <ThemedActionButton
                    :buttonColor="'error'"
                    :text="' Reset All Priority'"
                    @click="resetUserProjectsPriority"
                >
                </ThemedActionButton>
            </v-col>
            <v-col class="d-flex justify-center" cols="6">
                <v-dialog
                    transition="dialog-bottom-transition"
                    v-model="isDialogOpen"
                    width="100%"
                >
                    <template
                        v-slot:activator="{
                            props: activatorProps,
                        }"
                    >
                        <ThemedActionButton
                            :buttonVariant="'outlined'"
                            :text="'Assign Projects'"
                            v-bind="activatorProps"
                        >
                        </ThemedActionButton>
                    </template>

                    <template v-slot:default>
                        <v-card
                            style="
                                height: 100%;
                                display: flex;
                                flex-direction: column;
                            "
                        >
                            <!-- Fixed Toolbar -->
                            <v-toolbar
                                color="primary"
                                title="Assign Projects"
                                class="fixed-toolbar"
                            ></v-toolbar>
                            <!-- Assigned Projects as Chips -->
                            <v-card-text
                                class="pa-4"
                                style="
                                    display: flex;
                                    flex-wrap: wrap;
                                    gap: 8px;
                                "
                            >
                                <v-chip
                                    v-for="(
                                        project, index
                                    ) in tempSelectedProjects"
                                    :key="project.projectId"
                                    text-color="white"
                                    class="ma-1"
                                    @click:close="
                                        removeAssignedProject(
                                            project
                                        )
                                    "
                                    :closable="
                                        project.projectId !==
                                        activeEntry?.projectId &&
                                        !isProjectDisabledForTeamLead(project)
                                    "
                                >
                                    {{ project.name }}
                                </v-chip>
                            </v-card-text>

                            <!-- Scrollable Content -->
                            <v-card-text
                                class="text-h2"
                                style="
                                    overflow-y: auto;
                                    flex: 1;
                                    scrollbar-width: none;
                                "
                            >
                                <v-checkbox
                                    v-model="
                                        tempSelectedProjects
                                    "
                                    v-for="project in assignedProjects"
                                    :key="project.projectId"
                                    :label="project.name"
                                    :value="project"
                                    hide-details="true"
                                    density="compact"
                                    :disabled="
                                        project.projectId ===
                                        activeEntry?.projectId ||
                                        isProjectDisabledForTeamLead(project)
                                    "
                                />
                            </v-card-text>

                            <!-- Fixed Actions -->
                            <v-card-actions
                                class="d-flex justify-space-between pl-10 pr-10 fixed-actions"
                                style="margin-top: auto"
                            >
                                <v-btn
                                    text="Close"
                                    @click="closeDialog"
                                    >Close</v-btn
                                >
                                <v-btn
                                    color="primary"
                                    @click="submitSelection"
                                    >Save</v-btn
                                >
                            </v-card-actions>
                        </v-card>
                    </template>
                </v-dialog>
            </v-col>
        </template>
    </BottomButtonWrapper>
    </div>
</template>

<script>
// Vue imports removed - using Options API with mixin
import { mapGetters } from "vuex";
import {
    calculateTotalTimeWorked,
    calculateProjectsWorkSummaryForUser,
} from "@/utils/utils";
import ProjectsTable from "@/components/ProjectsTable.vue";
import ElapsedTimeDisplay from "./ElapsedTimeDisplay.vue";
import { VTabs, VTab, VCheckbox } from "vuetify/components";
import draggable from "vuedraggable";
import { ref, getDatabase, update, get } from "firebase/database";
import MessageSnackbar from "./MessageSnackbar.vue";
import { triggerSnackbar } from "@/utils/utils";
import OngoingProjectDetails from "./OngoingProjectDetails.vue";
import BottomButtonWrapper from "./BottomButtonWrapper.vue";
import ThemedActionButton from "./ThemedActionButton.vue";
import { logoutUser } from "@/services/firebaseService";
import EditableTimeEntries from "./EditableTimeEntries.vue";
import ProjectsPeriodFilterMenu from "@/components/ProjectsPeriodFilterMenu.vue";
import UserProfileForm from "@/components/UserProfileForm.vue";
import DataFetchLoadingComponent from "@/components/DataFetchLoadingComponent.vue";
import {
    periodSuffix,
    getDateRangeForPeriod,
    shouldFetchFromFirestore,
} from "@/utils/firestoreUtils";
import {
    projectMap,
    priorityMap,
    groupDescriptionsByProject,
    extendSummaryWithDescriptions,
    filterTimeEntriesForDate,
    getMergedTimeEntriesForUser,
} from "@/utils/utils";
import {
    isProjectActive,
    isProjectTeamLead,
} from "@/utils/projectUtils";
import DayNavigator from "./DayNavigator.vue";
import dayjs from "@/plugins/dayjs";
/**
 * @component
 * UserInfo component displays user information, including reports, and allows interactions like reordering projects.
 */

export default {
    name: "UserInfo",
    /**
     * Child components used in this component.
     */
    components: {
        ProjectsTable,
        ElapsedTimeDisplay,
        VTab,
        VTabs,
        draggable,
        MessageSnackbar,
        OngoingProjectDetails,
        BottomButtonWrapper,
        ThemedActionButton,
        EditableTimeEntries,
        ProjectsPeriodFilterMenu,
        DayNavigator,
        UserProfileForm,
        DataFetchLoadingComponent,
    },

    /**
     * Reactive data properties for the component.
     * @returns {Object} Reactive properties for the component's state.
     */
    data() {
        return {
            reportRows: [],
            selectedPeriod: "today",
            projectTabEntries: [],
            /**
             * Mutable copy of the monthly report data.
             * @type {Array}
             */
            mutableTableReportData: [],
            /**
             * Selected account status of the user.
             * @type {String|null}
             */
            selectedAccountStatus: null,

            /**
             * Tracks if the dialog is open.
             * @type {Boolean}
             * @default false
             */
            isDialogOpen: false,

            /**
             * Final selected projects (only updated on submission).
             * @type {Array}
             */
            selectedProjects: [],

            /**
             * Temporary projects for dialog interaction.
             * @type {Array}
             */
            tempSelectedProjects: [],

            /**
             * Backup of the selection before changes.
             * @type {Array}
             */
            backupSelectedProjects: [], // Backup of the selection before changes

            /**
             * The currently selected tab. Default is 'today'.
             * @type {String}
             * @default "today"
             */
            tab: "today", // Set default tab to 'today'
            /**
             * Snackbar object for managing UI notifications.
             * @type {Object}
             */
            snackbar: {
                show: false,
                message: "",
            },
            /**
             * Toggles the display of the user's description.
             * @type {Boolean}
             * @default false
             */
            showUserDescription: false,
            /**
             * Toggles editing mode for time entries.
             * @type {Boolean}
             * @default false
             */
            isEditingTimeEntries: false,
            /**
             * Stores the selected date for filtering time entries.
             * Defaults to the current day at midnight.
             * @type {Date}
             */
            entriesDate: dayjs().startOf("day").toDate(),
            /**
             * Loading state for Firestore data fetching.
             * @type {Boolean}
             * @default false
             */
            isLoadingFirestore: false,
        };
    },

    /**
     * Lifecycle hook called when the component is created.
     * Fetches projects if the user has the rights to view their own projects.
     */
    created() {
        if (this.getRights?.viewProjects?.includes("mine")) {
            this.$store.dispatch("fetchProjects");
        }
    },
    /**
     * Lifecycle hook called when the component is mounted.
     * Initializes mutableTableReportData with tableReportData.
     */
    mounted() {
        if (this.$route.query?.action === "edit-hours") {
            this.showEditableTimeEntries();
        }
        // load the default ("today") via the helper
        this.updateTableDataFor(this.selectedPeriod);

        const userProjectIds = this.user?.projects
            ? Object.keys(this.user.projects)
            : [];
        // Filter the assigned projects and sort them alphabetically by name
        this.tempSelectedProjects = this.assignedProjects
            .sort((a, b) => a.name.localeCompare(b.name)) // Sorting assigned projects
            .filter((project) => userProjectIds.includes(project.projectId));

        this.backupSelectedProjects = this.assignedProjects
            .sort((a, b) => a.name.localeCompare(b.name)) // Sorting assigned projects
            .filter((project) => userProjectIds.includes(project.projectId));
        if (this.user.accountStatus) {
            this.selectedAccountStatus =
                this.user.accountStatus.charAt(0).toUpperCase() +
                this.user.accountStatus.slice(1);
        }
        
    },

    watch: {
        /**
         * When switching to the "projects" tab, recompute the projects report data
         * so that it uses the correct `projectsForReport` logic for that tab
         * (i.e. only the viewed user's assigned projects, even for admins).
         */
        tab(newVal, oldVal) {
            if (newVal === "projects" && oldVal !== "projects") {
                this.updateTableDataFor(this.selectedPeriod);
            }
        },
    },

    computed: {
        ...mapGetters([
            "getAllProjects", // Retrieves all projects
            "getUsers", // Retrieves a list of all users.
            "getRights", // Retrieves user rights.
            "getManagedProjects", // Retrieves projects managed by the user.
        ]),
        rights() {
            return this.getRights;
        },
        extendedProjectsReportData() {
            const descMap = groupDescriptionsByProject(this.projectTabEntries);
            return extendSummaryWithDescriptions(
                this.mutableTableReportData,
                descMap
            );
        },
        extendedTodayReportData() {
            const descMap = groupDescriptionsByProject(this.currentTimeEntries);
            return extendSummaryWithDescriptions(this.dailyReportData, descMap);
        },
        currentTimeEntries() {
            if (!this.user?.timeEntries) return [];
            return filterTimeEntriesForDate(
                this.entriesDate,
                this.user.timeEntries
            );
        },
        /**
         * Formats the date as "Wed, May 28".
         * @returns {string}
         */
        formattedDate() {
            return this.entriesDate.toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
            });
        },
        /**
         * Retrieves the user's active time entry (the last one without an `endTime`).
         * @returns {Object|null} The active time entry, or null if none exists.
         */
        activeEntry() {
            if (this.user.timeEntries) {
                const entries = Object.entries(this.user.timeEntries).map(
                    ([key, entry]) => ({
                        ...entry, // Spread the entry properties
                        uid: key, // Include the `uid` (key from the database)
                    })
                );

                // Find the last entry without an end time
                return entries.findLast((entry) => !entry.endTime);
            }
            return null;
        },
        periodSuffixLabel() {
            return periodSuffix(this.selectedPeriod);
        },
        doProjectMap() {
            return projectMap(this.projectsForReport);
        },
        doPriorityMap() {
            return priorityMap(this.projectsForReport);
        },
        /**
         * Determines if the user is currently marked as "out of office".
         * @returns {Boolean} True if the user is out of office, otherwise false.
         */
        isUserOutOfOffice() {
            return this.user.userStatus === "outOfOffice";
        },
        /**
         * Gets projects available for assignment in the "Assign Projects" dialog.
         * - Admins: see ALL projects
         * - Team Leads: see their managed projects + user's assigned projects (non-managed ones will be disabled)
         * @returns {Array} Array of projects available for assignment.
         */
        assignedProjects() {
            const canViewAll = this.getRights?.viewProjects?.includes("all");

            if (canViewAll) {
                // Admin: show ALL projects
                const allProjects = this.getAllProjects || [];
                return allProjects
                    .filter((project) => project.status !== "systemProject")
                    .sort((a, b) => a.name.localeCompare(b.name));
            }

            // Team Lead: show managed projects + user's assigned projects
            const managedProjects = this.getManagedProjects || [];
            const userAssignedProjects =
                this.$store.getters.getProjectsForUser(this.userId) || [];

            // Create a map of managed project IDs for quick lookup
            const managedProjectIds = new Set(
                managedProjects.map((p) => p.projectId)
            );

            // Combine managed projects and user assigned projects
            const allAvailableProjects = [
                ...managedProjects,
                ...userAssignedProjects.filter(
                    (p) => !managedProjectIds.has(p.projectId)
                ),
            ];

            // Filter out system projects and sort alphabetically
            return allAvailableProjects
                .filter(
                    (project) =>
                        project.status !== "systemProject" &&
                        isProjectActive(project)
                )
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        /**
         * Checks if a project should be disabled (greyed out) for team leads.
         * A project is disabled if the viewer is not in the project's teamLeaders.
         * @param {Object} project - The project to check
         * @returns {boolean} True if the project should be disabled
         */
        isProjectDisabledForTeamLead() {
            return (project) => {
                const canViewAll = this.getRights?.viewProjects?.includes("all");
                // Admins can always assign/unassign
                if (canViewAll) return false;

                const currentUserId = this.$store.getters.getUser?.uid;
                if (!currentUserId) return false;

                const isTeamLeadOfProject = isProjectTeamLead(
                    project,
                    currentUserId
                );

                // If not team lead of project, disable it (grey it out)
                return !isTeamLeadOfProject;
            };
        },
        selectedDate() {
            return this.entriesDate;
        },
        // Compute minDate and maxDate based on the current month
        minDate() {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth(), 1);
        },
        maxDate() {
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 1, 0);
        },

        /**
         * Retrieves a list of projects filtered and prepared for reporting or editing.
         * Shows projects for the VIEWED USER, not the viewer's managed projects.
         *
         * - For admins: returns all projects (they can view everything)
         * - For team leads: returns projects assigned to the viewed user
         * - Includes projects with time entries (even if not assigned)
         * - Filters out lunch break when viewing (not editing)
         *
         * @returns {Array<Object>} A filtered list of projects for the viewed user.
         */
        projectsForReport() {
            const canViewAll = this.getRights?.viewProjects?.includes("all");

            // Base set: projects assigned to the VIEWED user
            // (used for all roles; this is the source of truth for user associations)
            const userAssignedProjects =
                this.$store.getters.getProjectsForUser(this.userId) || [];

            // 1) Projects tab: ALWAYS show only the viewed user's assigned projects,
            // regardless of viewer role (admin, teamLead, etc.)
            if (this.tab === "projects") {
                const excludeLunchBreak = !this.isEditingTimeEntries;
                return excludeLunchBreak
                    ? userAssignedProjects.filter(
                          (project) => project.projectId !== "SYSTEM_LUNCH-BREAK"
                      )
                    : userAssignedProjects;
            }

            // 2) Other tabs:
            //    - Admins: can still see all projects (getManagedProjects contains all for admins)
            //    - Non-admins: start from assigned projects and add projects with entries (name-resolved via getAllProjects)
            if (canViewAll) {
                const excludeLunchBreak = !this.isEditingTimeEntries;
                const allProjects = this.getManagedProjects || [];
                return excludeLunchBreak
                    ? allProjects.filter(
                          (project) => project.projectId !== "SYSTEM_LUNCH-BREAK"
                      )
                    : allProjects;
            }

            // 3) Non-admins for Today / editable views:
            // Get project IDs from time entries to include projects with entries (even if not assigned)
            let entriesToScan = [];
            if (this.tab === "today") {
                entriesToScan = this.currentTimeEntries || [];
            } else {
                entriesToScan = Object.values(this.user?.timeEntries || {});
            }

            const projectIdsWithEntries = new Set(
                entriesToScan.map((entry) => entry.projectId).filter(Boolean)
            );

            // Build result: start with assigned projects, add projects with entries
            const resultProjects = new Map();

            // Add all assigned projects
            userAssignedProjects.forEach((project) => {
                resultProjects.set(project.projectId, project);
            });

            // Add projects with time entries that aren't already included
            // Use getAllProjects for name resolution
            const allProjects = this.getAllProjects || [];
            projectIdsWithEntries.forEach((projectId) => {
                if (!resultProjects.has(projectId)) {
                    const project = allProjects.find(
                        (p) => p.projectId === projectId
                    );
                    if (project) {
                        resultProjects.set(projectId, project);
                    }
                }
            });

            // Filter based on editing mode (exclude lunch break when viewing)
            const excludeLunchBreak = !this.isEditingTimeEntries;
            const filtered = Array.from(resultProjects.values());

            return excludeLunchBreak
                ? filtered.filter(
                      (project) => project.projectId !== "SYSTEM_LUNCH-BREAK"
                  )
                : filtered;
        },

        /**
         * Retrieves the current user's information by their unique ID.
         * @returns {Object} User object or an empty object if not found.
         */
        user() {
            const allUsers = Array.isArray(this.getUsers)
                ? this.getUsers
                : Object.values(this.getUsers || {});
            return allUsers.find((user) => user.uid === this.userId) || {};
        },

        /**
         * User ID derived from the route parameters.
         * @returns {string} Unique user ID from the route.
         */
        userId() {
            return this.$route.params.uid;
        },

        /**
         * Data for rendering in the daily report time table.
         * @returns {Array<Object>} Processed table data.
         */
        dailyReportData() {
            return calculateProjectsWorkSummaryForUser(
                this.user,
                this.projectsForReport,
                {
                    date: this.entriesDate,
                    excludeEmpty: true,
                }
            );
        },

        /**
         * Computes the summary of projects for the selected date, filtered to exclude empty entries.
         *
         * - This method leverages `calculateProjectsWorkSummaryForUser` to process the user's time entries
         *   and projects relevant to the selected `entriesDate`.
         * - Returns an empty array if the user's time entries or projects are unavailable.
         *
         * @returns {Array<Object>} An array of project summaries for the selected date.
         *                          Each summary includes details such as project name,
         *                          total hours worked, and other metadata.
         */
        editHoursData() {
            if (!this.user.timeEntries || !this.projectsForReport.length) {
                return [];
            }
            const result = calculateProjectsWorkSummaryForUser(
                this.user,
                this.projectsForReport,
                {
                    date: this.entriesDate,
                    excludeEmpty: true,
                }
            );

            return result;
        },

        /**
         * Computes the total time worked for the current day.
         *
         * - This property calculates the total time worked by summing up the durations of the projects
         *   included in `dailyReportData`.
         * - It uses the `calculateTotalTimeWorked` utility function to perform the summation.
         *
         * @returns {string} A formatted string representing the total time worked (e.g., "8h 15m").
         *                   Returns an empty string or default value if `dailyReportData` is empty.
         */
        totalTimeWorked() {
            return calculateTotalTimeWorked(this.dailyReportData);
        },

        /**
         * Computes the total time worked for the currently editable time entries.
         *
         * - This property calculates the total time worked by summing up the durations of the projects
         *   included in `editHoursData`.
         * - It uses the `calculateTotalTimeWorked` utility function to perform the summation.
         *
         * @returns {string} A formatted string representing the total time worked (e.g., "5h 30m").
         *                   Returns an empty string or default value if `editHoursData` is empty.
         */

        totalTimeWorkedForEdit() {
            return calculateTotalTimeWorked(this.editHoursData);
        },

        /**
         * Retrieves all projects assigned to the viewed user.
         * Uses getProjectsForUser which gets data directly from user.projects.
         * @returns {Array<Object>} Array of project objects assigned to the viewed user.
         */
        allUsersProjects() {
            return this.$store.getters.getProjectsForUser(this.userId) || [];
        },

        /**
         * The project associated with the currently active time entry.
         * If a user has an active entry, the project must be assigned to them.
         * @returns {Object|null} Current project object or null if no active entry.
         */
        currentProject() {
            if (!this.activeEntry) {
                return null;
            }

            // Active entry means the project is assigned to the user
            // Use allUsersProjects which returns the user's assigned projects
            return (
                this.allUsersProjects.find(
                    (project) =>
                        project.projectId === this.activeEntry.projectId
                ) || null
            );
        },

        /**
         * Retrieves the description of the currently active time entry.
         *
         * - This property depends on the `activeEntry` computed property, which identifies the user's
         *   most recent time entry without an `endTime`.
         * - If an active entry exists, it checks for the presence of a description.
         * - If no description is available, it returns `null`.
         *
         * @returns {string|null} The description of the active entry, or `null` if no description is present
         *                        or no active entry exists.
         */
        activeEntryDescription() {
            const entry = this.activeEntry;
            if (entry) {
                return entry.description ? entry.description : null;
            }
        },
        /**
         * Determines if the footer should be shown.
         * Footer is shown on 'today' and 'projects' tabs, not on 'settings'.
         */
        shouldShowFooter() {
            return this.tab === 'today' || this.tab === 'projects';
        },
    },
    watch: {
        /**
         * When switching to the "projects" tab, recompute the projects report data
         */
        tab(newVal, oldVal) {
            if (newVal === "projects" && oldVal !== "projects") {
                this.updateTableDataFor(this.selectedPeriod);
            }
        },
    },

    methods: {
        goBack() {
            this.$router.back();
        },
        /**
         * Handles date changes from DayNavigator and recalculates both editable time entries
         * and report summaries for the selected date.
         * @param {Date} newDate - The newly selected date.
         */
        onDateChange(newDate) {
            this.entriesDate = newDate;
            // No need to manually touch dailyReportData; it updates via computed.
        },
        /**
         * Handles period change event from child component.
         * - Updates the selectedPeriod reactive property.
         * - Triggers table data refresh using the new period.
         *
         * @param {string} newPeriod - The newly selected period key.
         * @returns {void}
         */
        handlePeriodChange(newPeriod, customDateRange = null) {
            this.selectedPeriod = newPeriod;
            this.updateTableDataFor(newPeriod, customDateRange);
        },
        /**
         * Data for rendering in the report time table based on the period key
         * Fetches from Firestore and merges with RTDB if period extends beyond 45 days.
         * @param {string} periodKey - The period key
         * @param {Array<Date>|null} customDateRange - Custom date range array (for "custom" period)
         * @returns {Array<Object>} Processed table data for the period report.
         */
        async updateTableDataFor(periodKey, customDateRange = null) {
            const dateRange = getDateRangeForPeriod(periodKey, customDateRange);
            const { startDate: start, endDate: end } = dateRange;

            // Check if Firestore fetch is needed
            const needsFirestore = shouldFetchFromFirestore(
                periodKey,
                customDateRange
            );

            try {
                if (needsFirestore) {
                    this.isLoadingFirestore = true;
                }

                // Get merged time entries (RTDB + Firestore if needed)
                const mergedTimeEntries = await getMergedTimeEntriesForUser(
                    this.user,
                    periodKey,
                    customDateRange
                );

                // 1) Recalc summary using merged entries
                this.mutableTableReportData =
                    calculateProjectsWorkSummaryForUser(
                        this.user,
                        this.projectsForReport,
                        {
                            dateRange: {
                                startDate: new Date(start),
                                endDate: new Date(end),
                            },
                            excludeEmpty: false,
                            mergedTimeEntries, // Pass merged entries
                        }
                    );

                // 2) Use merged timeEntries for filtering descriptions
                this.projectTabEntries = filterTimeEntriesForDate(
                    [start, end], // Pass UTC milliseconds directly
                    mergedTimeEntries
                );
            } finally {
                this.isLoadingFirestore = false;
            }
        },
        /**
         * Exits the editing mode for time entries and resets related properties.
         *
         * - This method is called when the user exits the time entry editing mode.
         * - It resets the `isEditingTimeEntries` flag to `false`, clears the `currentTimeEntries`,
         *   and sets `entriesDate` back to the current day's date at midnight.
         *
         * @returns {void} This method does not return a value.
         */
        async exitEditMode() {
            this.isEditingTimeEntries = false;
            this.entriesDate = dayjs().startOf("day").toDate();
            // Refresh Projects tab data when exiting edit mode to reflect any overtime changes
            await this.updateTableDataFor(this.selectedPeriod);
        },
        /**
         * Toggles the visibility of the user description.
         *
         * This method inverts the value of `showUserDescription`,
         * switching it between `true` and `false`.
         *
         * @returns {void} This method does not return a value.
         */
        toggleDescriptionDisplay() {
            this.showUserDescription = !this.showUserDescription;
        },
        /**
         * Resets all project priorities for the current user to "Normal".
         *
         * This method retrieves the user's projects from Firebase, updates their priorities to "Normal",
         * and applies the changes both in Firebase and the local state. It also triggers a snackbar notification
         * to inform the user of the successful operation.
         *
         * @async
         * @throws {Error} Logs an error message to the console if the operation fails.
         */
        async resetUserProjectsPriority() {
            try {
                const db = getDatabase();
                const userProjectsRef = ref(
                    db,
                    `users/${this.userId}/projects`
                );

                // Get all user projects
                const userProjectsSnapshot = await get(userProjectsRef);
                const userProjects = userProjectsSnapshot.val();

                if (!userProjects) {
                    console.warn("No user projects found to reset.");
                    return;
                }

                // Prepare updates
                const updates = {};
                for (const projectId in userProjects) {
                    updates[`${projectId}/priority`] = "Normal";
                }

                // Apply updates to Firebase
                await update(userProjectsRef, updates);

                // Update local state to reflect the changes
                Object.keys(this.user.projects).forEach((projectId) => {
                    if (this.user.projects[projectId]) {
                        this.user.projects[projectId].priority = "Normal";
                    }
                });
                // Refresh the data to ensure the child component sees the updated priorities
                this.updateTableDataFor(this.selectedPeriod);
                triggerSnackbar(
                    this.snackbar,
                    'All priorities for this user have been reset to "Normal".',
                    "success"
                );
            } catch (error) {
                console.error(
                    "Error resetting user project priorities:",
                    error
                );
            }
        },
        /**
         * Updates the mutableTableReportData with new data and saves the project order to Firebase.
         * @param {Array} newData - The new data to update.
         */
        updateTableReportData(newData) {
            this.mutableTableReportData = [...newData];
            this.saveProjectOrderToFirebase(newData);
            if (this.getRights?.viewProjects?.includes("mine")) {
                this.$store.dispatch("fetchProjects");
            }
        },

        /**
         * Saves the project order to Firebase database.
         * @async
         * @param {Array} newData - The new project order data.
         */
        async saveProjectOrderToFirebase(newData) {
            try {
                // Create a new object for the user's projects with updated orders
                const updatedProjects = newData.reduce(
                    (acc, project, index) => {
                        acc[project.id] = {
                            ...this.user.projects[project.id], // Retain existing data for the project
                            order: index + 1, // Assign the order based on the checkbox sequence
                        };
                        return acc;
                    },
                    {}
                );

                // Firebase reference to the user's projects node
                const db = getDatabase();
                const userProjectsRef = ref(
                    db,
                    `users/${this.userId}/projects`
                );

                // Update the user's projects in Firebase
                await update(userProjectsRef, updatedProjects);

                // Refresh the project data if necessary
                if (this.getRights?.viewProjects?.includes("mine")) {
                    this.$store.dispatch("fetchProjects");
                }
            } catch (error) {
                console.error(
                    "Error updating project order in Firebase:",
                    error
                );
            }
        },

        /**
         * Updates the user's selected projects in Firebase, ensuring the "Lunch Break" project is always included.
         *
         * @async
         * @function submitSelection
         * @returns {Promise<void>} Resolves when the user's projects are successfully updated in Firebase.
         *
         * ### Functionality:
         * 1. Updates the user's selected projects based on `tempSelectedProjects`.
         * 2. Ensures the "Lunch Break" project (`SYSTEM_LUNCH-BREAK`) remains associated with the user.
         * 3. Removes unselected projects, except the "Lunch Break" project.
         * 4. Handles potential conflicts with active projects being removed.
         * 5. Synchronizes the updated project list with Firebase and local state.
         */
        async submitSelection() {
            try {
                const db = getDatabase();
                const userProjectsRef = ref(
                    db,
                    `users/${this.userId}/projects`
                );

                // Sort projects alphabetically before saving
                const selectedProjects = [...this.tempSelectedProjects].sort(
                    (a, b) => a.name.localeCompare(b.name)
                );

                // Initialize `updatedProjects` with selected projects
                const updatedProjects = selectedProjects.reduce(
                    (acc, project, index) => {
                        acc[project.projectId] = {
                            order:
                                this.user.projects?.[project.projectId]
                                    ?.order || index + 1, // Preserve existing order or assign a default
                            priority:
                                this.user.projects?.[project.projectId]
                                    ?.priority || "Normal", // Preserve priority or default to "Normal"
                            projectId: project.projectId, // Keep the project ID
                            name: project.name,
                        };
                        return acc;
                    },
                    {}
                );

                // Add "Lunch Break" project if it doesn't already exist
                const lunchBreakProjectRef = ref(
                    db,
                    "projects/SYSTEM_LUNCH-BREAK"
                );
                const lunchBreakProjectSnapshot = await get(
                    lunchBreakProjectRef
                );

                if (lunchBreakProjectSnapshot.exists()) {
                    const lunchBreakProjectId = lunchBreakProjectSnapshot.key;
                    const lunchBreakProjectData =
                        lunchBreakProjectSnapshot.val();

                    // Ensure the "Lunch Break" project is included in updatedProjects
                    updatedProjects[lunchBreakProjectId] = {
                        projectId: lunchBreakProjectId,
                        name: lunchBreakProjectData.name,
                        status: lunchBreakProjectData.status,
                        order:
                            this.user.projects?.[lunchBreakProjectId]?.order ||
                            99, // Default order
                        priority:
                            this.user.projects?.[lunchBreakProjectId]
                                ?.priority || "Normal", // Default priority
                    };
                } else {
                    console.warn(
                        "Lunch Break project not found in the database. Skipping its addition."
                    );
                }

                // If `this.user.projects` is undefined, there are no existing projects
                const existingProjects = this.user.projects || {};

                // Identify projects to be removed, excluding the "Lunch Break" project
                const removedProjects = Object.keys(existingProjects).filter(
                    (id) => !updatedProjects[id] && id !== "SYSTEM_LUNCH-BREAK" // Exclude "Lunch Break" from removal
                );

                // Mark removed projects with `null` for deletion in Firebase
                removedProjects.forEach((projectId) => {
                    updatedProjects[projectId] = null;
                });

                // Check if the user has an active entry on a project that is being removed
                const activeProjectId = this.activeEntry?.projectId;
                if (removedProjects.includes(activeProjectId)) {
                    return;
                }

                // Update Firebase with the new `projects` object
                await update(userProjectsRef, updatedProjects);
                // Ensure local state is sorted after saving
                this.tempSelectedProjects = selectedProjects;

                // Close dialog and update local state
                this.isDialogOpen = false;
                this.mutableTableReportData =
                    calculateProjectsWorkSummaryForUser(
                        this.user,
                        this.projectsForReport
                    );
                if (this.getRights?.viewProjects?.includes("mine")) {
                    this.$store.dispatch("fetchProjects");
                }
            } catch (error) {
                console.error("Error updating projects in Firebase:", error);
            }
        },

        /**
         * Removes a project from the list of temporarily selected projects.
         * This method is triggered when a user clicks the close button on a project chip.
         *
         * @async
         * @param {object} project - The project object to be removed.
         * @param {string} project.projectId - The unique identifier of the project.
         * @param {string} project.name - The name of the project.
         * @throws {Error} Logs an error if the removal process encounters an issue.
         */
        async removeAssignedProject(project) {
            try {
                const activeProjectId = this.activeEntry?.projectId;

                if (project.projectId === activeProjectId) {
                    return;
                }

                // Prevent removal if project is disabled for team leads
                if (this.isProjectDisabledForTeamLead(project)) {
                    return;
                }

                // Remove the project from tempSelectedProjects
                this.tempSelectedProjects = this.tempSelectedProjects.filter(
                    (p) => p.projectId !== project.projectId
                );
            } catch (error) {
                console.error("Error removing assigned project:", error);
            }
        },

        /**
         * Closes the project selection dialog and restores the previously selected projects.
         * If the user cancels without saving, this method ensures that no unintended changes are kept.
         */
        closeDialog() {
            // Restore the original selection if closing without saving
            this.tempSelectedProjects = [...this.backupSelectedProjects];
            // Discard temporary selections and close the dialog
            this.isDialogOpen = false;
        },

        /**
         * Updates the priority of a specific project in Firebase and the local state.
         *
         * This method updates the `priority` field of a given project in the Firebase Realtime Database
         * and ensures the local state reflects the change. It is designed to handle priority updates for
         * user-specific projects efficiently by only modifying the `priority` field.
         *
         * @async
         * @param {Object} params - The parameters for the priority update.
         * @param {Object} params.item - The project item to be updated.
         * @param {string} params.item.id - The unique identifier of the project in Firebase.
         * @param {string} params.item.projectId - The unique identifier of the project in the local state.
         * @param {string} params.newPriority - The new priority value to be set (e.g., "High", "Normal").
         * @throws {Error} Logs an error to the console if the update fails in Firebase.
         */
        async handlePriorityUpdate({ item, newPriority }) {
            try {
                const db = getDatabase();
                const projectPath = `users/${this.userId}/projects/${item.id}`;

                // Update the priority field in Firebase for the existing project
                const projectRef = ref(db, projectPath);

                // Only update the `priority` field without overwriting other data
                await update(projectRef, {
                    priority: newPriority,
                });
            } catch (error) {
                console.error(
                    "Error updating project priority in Firebase:",
                    error
                );
            }
        },

        /**
         * Handles changes to the user's account status.
         *
         * This method compares the new status with the current status, performs necessary
         * updates (such as ending active time entries, updating the status in Vuex and Firebase),
         * and logs out the user if their status is set to "inactive". After processing, it redirects
         * the user to the "UserDashboard" with the appropriate query parameter.
         *
         * @async
         * @param {string} newStatus - The new account status to set (e.g., "active", "inactive").
         *
         * @throws {Error} If the logout process fails when the account status is set to "inactive".
         *
         * @returns {Promise<void>} Resolves when the account status update and related processes are complete.
         */
        async handleAccountStatusChange(newStatus) {
            const oldStatus = this.user.accountStatus;

            if (newStatus !== oldStatus && newStatus) {
                const activeEntry = this.activeEntry;
                if (activeEntry) {
                    await this.$store.dispatch("endCurrentTimeEntry", {
                        isEndOfDay: true,
                        activeEntry: activeEntry,
                        userId: this.userId,
                    });
                }

                await this.$store.dispatch("endCurrentProject", true);

                // Convert newStatus to lowercase before dispatching
                const lowercaseStatus = newStatus.toLowerCase();
                const userId = this.userId; // Get userId from route params
                if (userId) {
                    await this.$store.dispatch("updateAccountStatus", {
                        userId,
                        status: lowercaseStatus, // Send the lowercase status to Vuex
                    });
                    // If the new status is "inactive," log out the user
                    if (lowercaseStatus === "inactive") {
                        try {
                            await logoutUser({ uid: userId });
                        } catch (error) {
                            console.error(
                                "[handleAccountStatusChange] Error during logout:",
                                error
                            );
                        }
                    }
                    this.$router.push({
                        name: "UserDashboard",
                        query: { tab: "users" },
                    });
                } else {
                    console.error("User ID is undefined.");
                }

                // Update local state
                this.selectedAccountStatus = newStatus;
            }
        },
        /**
         * Displays the editable time entries for the selected date.
         *
         * This method filters the user's time entries for the selected date, sets the filtered entries as the
         * current time entries for editing, and toggles the `isEditingTimeEntries` flag to `true`.
         *
         * @returns {void} This method does not return a value.
         */
        showEditableTimeEntries() {
            this.isEditingTimeEntries = true;
        },
    },
};
</script>

<style scoped>
.fixed-toolbar {
    position: sticky;
    top: 0;
    z-index: 1; /* Ensure it stays above the scrollable content */
}

.fixed-actions {
    position: sticky;
    bottom: 0;
    z-index: 1; /* Ensure it stays above the scrollable content */
    background: white; /* Add background to prevent transparency */
}
</style>
